import type { DescriptionHeading } from "@/iframe_messages";

const HEADINGS = ".description-content :is(h1, h2, h3, h4, h5, h6)";
// A heading in a box titles the box, not a section of the description.
const BOXES = ".callout, details";
// What shows without text of its own, and what has text that does not show.
const MEDIA = "img, svg, picture, video, audio, canvas, iframe, table, pre";
const UNSEEN = "style, script, template, link, meta, noscript";

// The text of a heading as a reader sees it. KaTeX also renders math as hidden MathML, which would repeat it.
function headingText(heading: HTMLElement): string {
    const clone = heading.cloneNode(true) as HTMLElement;
    clone.querySelectorAll(".katex-mathml").forEach((el) => el.remove());
    return clone.textContent.replace(/\s+/g, " ").trim();
}

// The id kramdown's GFM parser gives a Markdown heading, so headings of HTML descriptions get ids alike.
export function headingId(text: string): string {
    return (
        text
            .toLowerCase()
            .replace(/[^\p{L}\p{M}\p{N}\p{Pc}\- ]/gu, "")
            .replaceAll(" ", "-") || "section"
    );
}

function uniqueId(base: string): string {
    let id = base;
    for (let i = 1; document.getElementById(id); i++) id = `${base}-${i}`;
    return id;
}

// The headings of the sections of the description, without the titles of callouts and spoilers.
function sectionHeadings(): { element: HTMLElement; text: string }[] {
    return Array.from(document.querySelectorAll<HTMLElement>(HEADINGS))
        .filter((element) => !element.closest(BOXES))
        .map((element) => ({ element, text: headingText(element) }))
        .filter(({ text }) => text.length > 0);
}

// Gives every section heading without an id one, as Markdown headings already have.
function addHeadingIds(): void {
    for (const { element, text } of sectionHeadings()) {
        // getElementById returns the first element with an id, so a heading that isn't it shares its id.
        if (!element.id || document.getElementById(element.id) !== element) element.id = uniqueId(headingId(text));
    }
}

// The section headings with the level of their tag and their offset from the top of the description.
function collectHeadings(sections: { element: HTMLElement; text: string }[]): DescriptionHeading[] {
    return sections.map(({ element, text }) => ({
        id: element.id,
        text,
        level: Number(element.tagName[1]),
        top: Math.round(element.getBoundingClientRect().top + window.scrollY),
    }));
}

/**
 * Whether the description shows something before its first section heading, or anything at all
 * without one, for which the page links to its top.
 */
function hasIntroduction(first: HTMLElement | undefined): boolean {
    const content = document.querySelector(".description-content");
    if (!content) return false;
    const range = document.createRange();
    range.selectNodeContents(content);
    if (first) range.setEndBefore(first);
    const before = range.cloneContents();
    before.querySelectorAll(UNSEEN).forEach((element) => element.remove());
    return before.textContent.trim() !== "" || before.querySelector(MEDIA) !== null;
}

// Moves the keyboard focus to a heading, so reading and tabbing go on from there.
export function focusHeading(id: string): void {
    const heading = document.getElementById(id);
    if (!heading) return;
    if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
    heading.focus({ preventScroll: true });
}

/**
 * Sends the headings, and whether something comes before the first, to the page now and again
 * whenever the description changes height, because images that load and spoilers that open move
 * the headings below them.
 */
export function watchHeadings(send: (headings: DescriptionHeading[], intro: boolean) => void): void {
    addHeadingIds();
    let last = "";
    let frame: number | null = null;
    const report = (): void => {
        frame = null;
        const sections = sectionHeadings();
        const headings = collectHeadings(sections);
        const intro = hasIntroduction(sections[0]?.element);
        const serialized = JSON.stringify([headings, intro]);
        if (serialized === last) return;
        last = serialized;
        send(headings, intro);
    };
    report();
    new ResizeObserver(() => {
        frame ??= requestAnimationFrame(report);
    }).observe(document.body);
}
